import React from 'react'
import {
  AlertTriangle,
  Shield,
  TrendingUp,
  Activity,
  Target,
  DollarSign
} from 'lucide-react'
import type { RiskMetrics } from '@/types/risk-assessment'

interface RiskScoreOverviewProps {
  riskMetrics: RiskMetrics
  portfolioValue: number
  propertyCount: number
}

export function RiskScoreOverview({ riskMetrics, portfolioValue, propertyCount }: RiskScoreOverviewProps) {
  // Defensive checks for riskMetrics
  if (!riskMetrics) {
    console.error('RiskScoreOverview: riskMetrics is undefined or null')
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="text-center text-gray-500">
          <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
          <p>Risk data not available</p>
        </div>
      </div>
    )
  }

  const getRiskLevelDisplay = (score: number) => {
    if (score <= 30) return { level: 'Low Risk', color: 'green', icon: Shield }
    if (score <= 60) return { level: 'Moderate Risk', color: 'yellow', icon: Activity }
    if (score <= 80) return { level: 'High Risk', color: 'orange', icon: AlertTriangle }
    return { level: 'Critical Risk', color: 'red', icon: AlertTriangle }
  }

  // Ensure overall_risk_score is a valid number
  const overallScore = typeof riskMetrics.overall_risk_score === 'number' ? riskMetrics.overall_risk_score : 50
  console.log('🎯 RiskScoreOverview - Displaying overall score:', overallScore, 'from riskMetrics:', riskMetrics)
  const riskDisplay = getRiskLevelDisplay(overallScore)
  const RiskIcon = riskDisplay.icon

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Portfolio Risk Overview</h3>
        <div className="flex items-center space-x-3">
          <span className="text-sm text-gray-500">Last Updated: {new Date().toLocaleDateString()}</span>
        </div>
      </div>

      {/* Overall Risk Score */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="col-span-1 md:col-span-2">
          <div className="text-center p-6 bg-gray-50 rounded-lg">
            <div className="flex justify-center mb-4">
              <div className="relative">
                <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 100 100">
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="#e5e7eb"
                    strokeWidth="8"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke={
                      riskDisplay.color === 'green' ? '#10b981' :
                      riskDisplay.color === 'yellow' ? '#f59e0b' :
                      riskDisplay.color === 'orange' ? '#f97316' : '#ef4444'
                    }
                    strokeWidth="8"
                    strokeLinecap="round"
                    strokeDasharray={`${overallScore * 2.51} 251`}
                    className="transition-all duration-500"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-900">
                      {overallScore}
                    </div>
                    <div className="text-sm text-gray-500">Risk Score</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-center space-x-2">
              <RiskIcon className={`w-5 h-5 ${
                riskDisplay.color === 'green' ? 'text-green-600' :
                riskDisplay.color === 'yellow' ? 'text-yellow-600' :
                riskDisplay.color === 'orange' ? 'text-orange-600' : 'text-red-600'
              }`} />
              <span className={`font-medium ${
                riskDisplay.color === 'green' ? 'text-green-700' :
                riskDisplay.color === 'yellow' ? 'text-yellow-700' :
                riskDisplay.color === 'orange' ? 'text-orange-700' : 'text-red-700'
              }`}>
                {riskDisplay.level}
              </span>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              Based on concentration, leverage, and market factors
            </p>
          </div>
        </div>

        {/* Portfolio Summary */}
        <div className="col-span-1 md:col-span-2 space-y-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <DollarSign className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium text-blue-900">Portfolio Value</span>
              </div>
              <span className="text-lg font-bold text-blue-900">
                {formatCurrency(portfolioValue)}
              </span>
            </div>
          </div>

          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Target className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium text-green-900">Properties</span>
              </div>
              <span className="text-lg font-bold text-green-900">
                {propertyCount}
              </span>
            </div>
          </div>

          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <TrendingUp className="w-5 h-5 text-purple-600" />
                <span className="text-sm font-medium text-purple-900">Leverage Ratio</span>
              </div>
              <span className="text-lg font-bold text-purple-900">
                {formatPercentage(riskMetrics.leverage_ratio || 0)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Risk Factor Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Concentration Risk</span>
            <span className="text-sm font-bold text-gray-900">
              {riskMetrics.concentration_risk?.geographic?.concentration_score || 0}/100
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full ${
                (riskMetrics.concentration_risk?.geographic?.concentration_score || 0) <= 30 ? 'bg-green-500' :
                (riskMetrics.concentration_risk?.geographic?.concentration_score || 0) <= 60 ? 'bg-yellow-500' :
                (riskMetrics.concentration_risk?.geographic?.concentration_score || 0) <= 80 ? 'bg-orange-500' : 'bg-red-500'
              }`}
              style={{ width: `${riskMetrics.concentration_risk?.geographic?.concentration_score || 0}%` }}
            />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Liquidity Score</span>
            <span className="text-sm font-bold text-gray-900">
              {riskMetrics.liquidity_score || 0}/100
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full ${
                riskMetrics.liquidity_score >= 70 ? 'bg-green-500' :
                riskMetrics.liquidity_score >= 40 ? 'bg-yellow-500' :
                riskMetrics.liquidity_score >= 20 ? 'bg-orange-500' : 'bg-red-500'
              }`}
              style={{ width: `${riskMetrics.liquidity_score || 0}%` }}
            />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Vacancy Risk</span>
            <span className="text-sm font-bold text-gray-900">
              {riskMetrics.vacancy_risk_score || 0}/100
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full ${
                riskMetrics.vacancy_risk_score <= 20 ? 'bg-green-500' :
                riskMetrics.vacancy_risk_score <= 40 ? 'bg-yellow-500' :
                riskMetrics.vacancy_risk_score <= 60 ? 'bg-orange-500' : 'bg-red-500'
              }`}
              style={{ width: `${riskMetrics.vacancy_risk_score || 0}%` }}
            />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Market Correlation</span>
            <span className="text-sm font-bold text-gray-900">
              {formatPercentage((riskMetrics.market_correlation || 0) * 100)}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full ${
                riskMetrics.market_correlation <= 0.3 ? 'bg-green-500' :
                riskMetrics.market_correlation <= 0.6 ? 'bg-yellow-500' :
                riskMetrics.market_correlation <= 0.8 ? 'bg-orange-500' : 'bg-red-500'
              }`}
              style={{ width: `${(riskMetrics.market_correlation || 0) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Risk Insights */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">Risk Assessment Insights</h4>
        <div className="space-y-1 text-sm text-blue-800">
          {overallScore > 70 && (
            <p>• High risk detected - consider diversification strategies</p>
          )}
          {(riskMetrics.concentration_risk?.geographic?.concentration_score || 0) > 60 && (
            <p>• Geographic concentration risk - expand to new markets</p>
          )}
          {(riskMetrics.leverage_ratio || 0) > 80 && (
            <p>• High leverage ratio - monitor debt levels closely</p>
          )}
          {(riskMetrics.liquidity_score || 0) < 40 && (
            <p>• Low liquidity - maintain adequate cash reserves</p>
          )}
          {overallScore <= 40 && (
            <p>• Portfolio shows healthy risk diversification</p>
          )}
        </div>
      </div>
    </div>
  )
}